/**
 * InstallChecklistAdmin — INSTALL-CHECK-01 (Seth 2026-09-25): Settings → Install
 * Checklist. The lines installers answer on Wrap Up — the job packet's Install
 * Order, word for word to start. Each line shows only when the job has the
 * equipment picked here (none picked = every install): a furnace-only job never
 * shows "Condensing Unit Installed & Level". Reword, reorder, add, archive —
 * no code edits (the admin-self-service rule).
 */
import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Check, ClipboardList, Pencil, Plus, RotateCcw, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useEquipmentCategories } from "./equipmentCategories";
import {
  useInstallCheckItems, useSaveCheckItem, useMoveCheckItem, type InstallCheckItem, type CheckKind,
} from "@/features/jobs/installChecklist";

const KIND_LABEL: Record<CheckKind, string> = { ynna: "Yes / No / N/A", text: "Text", number: "Number" };

export function InstallChecklistAdmin() {
  const { data, isLoading } = useInstallCheckItems({ all: true });
  const { data: cats } = useEquipmentCategories();
  const save = useSaveCheckItem();
  const move = useMoveCheckItem();
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<InstallCheckItem | null>(null);

  const rows = data ?? [];
  const active = rows.filter((r) => r.active);
  const archived = rows.filter((r) => !r.active);
  const catLabel = useMemo(() => new Map((cats ?? []).map((c) => [c.key, c.label])), [cats]);
  const catList = (cats ?? []).filter((c) => c.active);

  const startEdit = (it: InstallCheckItem) => { setEditId(it.id); setDraft({ ...it }); };
  const toggleCat = (k: string) => setDraft((d) => d ? { ...d, applies_to: d.applies_to.includes(k) ? d.applies_to.filter((x) => x !== k) : [...d.applies_to, k] } : d);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-emerald-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
            <ClipboardList className="h-3.5 w-3.5" />
          </span>
          <span className="text-[13px] font-bold text-slate-800">Install Checklist</span>
          <InfoTip label="About the install checklist">
            A line shows only on jobs with the equipment you pick. A <b>No</b> goes to the office with the installer&apos;s note.
            Who soldered the line set, the leak test and the microns are asked on Log the Charge.
          </InfoTip>
        </div>
        <button type="button" onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 transition-colors hover:text-brand-900">
          <Plus className="h-3 w-3" /> Add a line
        </button>
      </div>

      <div className="px-4 py-3">
        {adding && (
          <form onSubmit={(e) => { e.preventDefault(); save.mutate({ label: newLabel, sort: (active.length + 1) * 10 }, { onSuccess: () => { setNewLabel(""); setAdding(false); } }); }}
            className="mb-3 flex items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2">
            <input autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Filter Size Written on the Furnace"
              className="min-w-[200px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30" />
            <button type="submit" disabled={!newLabel.trim() || save.isPending} className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">
              {save.isPending ? "Saving…" : "Add"}
            </button>
          </form>
        )}

        {isLoading ? (
          <div className="space-y-1.5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : (
          <ol className="divide-y divide-slate-100">
            {active.map((it, i) => (
              <li key={it.id} className="group py-1.5">
                {editId === it.id && draft ? (
                  <form onSubmit={(e) => { e.preventDefault(); save.mutate(draft, { onSuccess: () => { setEditId(null); setDraft(null); } }); }}
                    className="space-y-2 rounded-lg border border-hairline bg-slate-50/60 p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <input autoFocus value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        aria-label="Line" className="min-w-[220px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" />
                      <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as CheckKind })} aria-label="Answer"
                        className="rounded-md border border-hairline bg-white px-2 py-1.5 text-xs text-slate-700">
                        {(Object.keys(KIND_LABEL) as CheckKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                      </select>
                      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <input type="checkbox" checked={draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} /> Must be answered
                      </label>
                    </div>
                    <div>
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Shows when the job has</div>
                      <div className="flex flex-wrap gap-1">
                        {catList.map((c) => (
                          <button key={c.key} type="button" aria-pressed={draft.applies_to.includes(c.key)} onClick={() => toggleCat(c.key)}
                            className={cn("rounded-full border px-2 py-0.5 text-[11px] font-medium",
                              draft.applies_to.includes(c.key) ? "border-brand-600 bg-brand-600 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                      <p className="mt-1 text-[11px] text-slate-400">{draft.applies_to.length ? "" : "None picked — every install shows it."}</p>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" disabled={!draft.label.trim() || save.isPending} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white disabled:bg-brand-300"><Check className="h-3 w-3" /> Save</button>
                      <button type="button" onClick={() => { setEditId(null); setDraft(null); }} className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs text-slate-600">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="w-6 shrink-0 text-right text-[11px] font-semibold tabular-nums text-slate-300">{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">{it.label}</span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {KIND_LABEL[it.kind]} · {it.applies_to.length ? it.applies_to.map((k) => catLabel.get(k) ?? k).join(", ") : "Every install"}{it.required ? "" : " · optional"}
                      </span>
                    </span>
                    <button type="button" disabled={i === 0 || move.isPending} onClick={() => move.mutate({ list: active, id: it.id, dir: -1 })} title="Move up" aria-label="Move up"
                      className="rounded p-1 text-slate-300 opacity-40 hover:text-slate-600 disabled:opacity-10 group-hover:opacity-100"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" disabled={i === active.length - 1 || move.isPending} onClick={() => move.mutate({ list: active, id: it.id, dir: 1 })} title="Move down" aria-label="Move down"
                      className="rounded p-1 text-slate-300 opacity-40 hover:text-slate-600 disabled:opacity-10 group-hover:opacity-100"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => startEdit(it)} title="Edit" aria-label={`Edit ${it.label}`}
                      className="rounded p-1 text-slate-300 opacity-40 hover:text-slate-600 group-hover:opacity-100"><Pencil className="h-3 w-3" /></button>
                    <button type="button" onClick={() => save.mutate({ ...it, active: false })} title="Archive — installers stop seeing it" aria-label={`Archive ${it.label}`}
                      className="rounded p-1 text-slate-300 opacity-40 hover:text-red-500 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}

        {archived.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Archived</p>
            <ul className="space-y-1">
              {archived.map((it) => (
                <li key={it.id} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="min-w-0 flex-1 truncate line-through">{it.label}</span>
                  <button type="button" onClick={() => save.mutate({ ...it, active: true })} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50">
                    <RotateCcw className="h-3 w-3" /> Restore
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
